import React, { useEffect, useState } from "react";
import { Users, GraduationCap, CalendarClock, PlayCircle, CheckCircle2, Rocket, PhoneCall, UserX } from "lucide-react";
import api, { errMsg } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { STAGES } from "./PatriotPanel";

function StageEditor({ p, onSaved }) {
  const { toast } = useToast();
  const [stage, setStage] = useState(p.stage || "Not Registered");
  const [date, setDate] = useState(p.training_scheduled_date || "");
  const [source, setSource] = useState(p.training_source || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/workforce/${p.contractor_id}/status`, { stage, training_scheduled_date: date || null, training_source: source || null });
      toast({ title: "Training status saved", description: "The contractor was notified." });
      onSaved();
    } catch (e) { toast({ title: "Save failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={stage} onChange={(e) => setStage(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`stage-select-${p.contractor_id}`}>
        {STAGES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`stage-date-${p.contractor_id}`} />
      <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source (e.g. client email)" className="w-36 rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`stage-source-${p.contractor_id}`} />
      <Button size="sm" disabled={busy} onClick={save} data-testid={`stage-save-${p.contractor_id}`}>{busy ? "…" : "Save"}</Button>
    </div>
  );
}

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
}

const ICONS = {
  registered: GraduationCap, scheduled: CalendarClock, in_training: PlayCircle,
  completed: CheckCircle2, ready: Rocket, active_phone: PhoneCall,
  active_workforce: Users, not_active: UserX,
};

export default function WorkforceDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);
  const [loadingDrill, setLoadingDrill] = useState(false);

  const reload = () => api.get("/admin/workforce").then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Unable to load workforce dashboard"));
  useEffect(() => { reload(); }, []);

  const openDrill = async (card) => {
    if (card.key === "active_phone" && card.awaiting_production_data) return;
    setLoadingDrill(true);
    setDrill({ card, people: [] });
    try {
      const r = await api.get(`/admin/workforce/list?bucket=${card.key}`);
      setDrill({ card, people: r.data.people });
    } catch (e) {
      setDrill({ card, people: [], error: e.response?.data?.detail || "Unable to load list" });
    } finally {
      setLoadingDrill(false);
    }
  };

  if (error) return <div className="text-sm text-rose-600" data-testid="workforce-error">{error}</div>;
  if (!data) return <div className="text-sm text-slate-400">Loading workforce…</div>;

  return (
    <div className="space-y-6" data-testid="workforce-dashboard">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Campbell Communications Workforce</h2>
        <p className="text-sm text-slate-500">Live counts from real records across {data.total_people} internal people. Click any card to see who's behind the number.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.cards.map((c) => {
          const Icon = ICONS[c.key] || Users;
          const awaiting = c.key === "active_phone" && c.awaiting_production_data;
          return (
            <Card
              key={c.key}
              data-testid={`workforce-card-${c.key}`}
              onClick={() => openDrill(c)}
              className={awaiting ? "opacity-90" : "cursor-pointer transition-shadow hover:shadow-md"}
            >
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</span>
                <Icon className="h-4 w-4 text-violet-500" />
              </div>
              {awaiting ? (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-amber-600" data-testid="awaiting-production-data">Awaiting production data</div>
                  <div className="mt-1 text-xs text-slate-400">No call-platform integration connected yet.</div>
                </div>
              ) : (
                <div className="mt-3 text-3xl font-bold text-slate-900">{c.count}</div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle data-testid="drill-title">{drill?.card?.label} {drill && !loadingDrill ? `(${drill.people.length})` : ""}</DialogTitle>
          </DialogHeader>
          {loadingDrill ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : drill?.error ? (
            <div className="py-4 text-sm text-rose-600">{drill.error}</div>
          ) : drill?.people?.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No people in this group yet.</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Client</th><th className="py-2 pr-4">Program</th><th className="py-2 pr-4">Supervisor</th><th className="py-2 pr-4" colSpan={2}>Stage · scheduled date · source</th></tr>
                </thead>
                <tbody>
                  {drill?.people?.map((p) => (
                    <tr key={p.contractor_id} className="border-b border-slate-50 align-top" data-testid={`drill-row-${p.contractor_id}`}>
                      <td className="py-2 pr-4"><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-400">{p.email}</div></td>
                      <td className="py-2 pr-4 text-slate-600">{p.client || "—"}</td>
                      <td className="py-2 pr-4 text-slate-600">{p.program || "—"}</td>
                      <td className="py-2 pr-4 text-slate-600">{p.supervisor || "—"}</td>
                      <td className="py-2 pr-4 text-slate-600" colSpan={2}><StageEditor p={p} onSaved={() => { reload(); openDrill(drill.card); }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
